import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import TicketItem from '../TicketItem';
import SellTicketsHeader from '../SellTicketsHeader';
import SellTicketsLayout from '../SellTicketsLayout';
import extractEventId from '../../utils/helpers';
import withAsyncLifecycle from '../../../core/hoc/async-lifecycle';
import formatFloatWithSuperscript from '../../../core/utils/helpers';
import { selectSellFormData, selectSellSelectedEvent, selectSellTickets } from '../../selectors/sell-tickets';
import { selectHttpInfo } from '../../../core/selectors/http';
import { setFormById } from '../../actions/forms';
import { getTickets, saveSellPriceTickets, unselectTicket, SELL_GET_TICKETS_REQUEST_NAME } from '../../actions/sell-tickets';
import { SELL_TICKET_FORM } from '../../reducers/features';
import { SELL_STEP_2, SELL_STEP_3, SELL_STEP_4 } from '../../constants/steps';

const SellStep3 = () => {
    const { isFetching, isError } = useSelector((state) => selectHttpInfo(state, SELL_GET_TICKETS_REQUEST_NAME));
    const tickets = useSelector(selectSellTickets)
        .filter((ticket) => ticket.isSelected);
    const dispatch = useDispatch();
    useEffect(() => {
        const eventId = extractEventId();
        dispatch(getTickets(SELL_STEP_3, eventId));
    }, []);
    const navigate = useNavigate();
    if (!isFetching && tickets.length === 0) {
        navigate(`/sell/${SELL_STEP_2}`);
    }
    const SellStep3WithAsyncLifecycle = withAsyncLifecycle(SellStep3Content, { isFetching, isError });
    return <SellStep3WithAsyncLifecycle tickets={tickets}/>;
};

const SellStep3Content = ({ tickets }) => {
    const inputs = useSelector((state) => selectSellFormData(state, SELL_TICKET_FORM));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const event = useSelector(selectSellSelectedEvent);
    const goToNextStep = () => navigate(`/sell/${SELL_STEP_4}`);
    const errorNotify = () => toast.error(intl.formatMessage({ id: 'notify_error_message' }));
    const preventMinus = (e) => {
        if (e.key === '-' || e.key === 'e') {
            e.preventDefault();
        }
    };

    return (
        <SellTicketsLayout>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <SellTicketsHeader title={<FormattedMessage id="sell_add_tickets_sell_price"/>}
                        subtitle={intl.formatMessage({ id: 'sell_add_tickets_sell_price_desc' })}/>
                    <div className="tickets-container mb-3">
                        {tickets.map((ticket) => (
                            <div className="ticket-container border rounded mb-3" key={ticket.code}>
                                <Row>
                                    <Col lg={{ span: 8 }} md={12}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <TicketItem ticket={ticket} event={event}/>
                                            <span className="btn-trash"
                                                onClick={() => dispatch(unselectTicket(ticket.code, errorNotify))}>
                                                <i className="bi bi-trash"/>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col lg={{ span: 4 }} md={12} className="ticket-container-actions">
                                        <div className="ticket_old_price mb-3"><FormattedMessage
                                            id="buy_price"/>: {formatFloatWithSuperscript(ticket.buyPrice)} {ticket.currency}
                                        </div>
                                        <div className="ticket_new_price bg-primary-subtle p-3 rounded">
                                            <FormattedMessage id="sell_enter_sell_price"/>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="999999"
                                                    value={inputs[`${ticket.code}`].sellPrice} key={ticket.code}
                                                    controlId={`sellPrice-${ticket.code}`}
                                                    isInvalid={!inputs[`${ticket.code}`].isValid}
                                                    onKeyDown={preventMinus}
                                                    placeholder={intl.formatMessage(
                                                        { id: 'buy_price_placeholder' },
                                                        {
                                                            price: ticket.sellPriceMax,
                                                        },
                                                    )}
                                                    onChange={(e) => dispatch(setFormById(
                                                        SELL_TICKET_FORM,
                                                        ticket.code,
                                                        {
                                                            sellPrice: e.target.value,
                                                            isValid: true,
                                                            errorMessage: null,
                                                        },
                                                    ))}
                                                />
                                                <InputGroup.Text id="currency">{ticket.currency}</InputGroup.Text>
                                            </InputGroup>
                                            {inputs[`${ticket.code}`].errorMessage && <span
                                                className="text-danger">{inputs[`${ticket.code}`].errorMessage} {ticket.currency}</span>}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <div className="step-actions d-flex mt-3 mb-5">
                        <Button variant="link" className="text-decoration-none me-2" onClick={() => navigate('/sell/2')}>
                            <FormattedMessage id="prev"/>
                        </Button>
                        <Button variant="primary" size="lg" onClick={() => dispatch(saveSellPriceTickets(inputs, goToNextStep))}>
                            <FormattedMessage id="continue"/>
                        </Button>
                    </div>
                </Col>
            </Row>
        </SellTicketsLayout>
    );
};
export default SellStep3;
